import { SharePanel } from '@/components/FormShare/SharePanel'
import { FormQuickActionsMenu } from '@/components/Forms/FormQuickActionsMenu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { FormFolderContract, FormUiContract } from '@/contracts/questionnaire'
import { formatRelativeTime } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Edit3, Eye, Share2 } from 'lucide-react'
import type { FormListActionHandlers } from './types'

const statusMap = {
  draft: { label: '草稿', variant: 'secondary' },
  published: { label: '上線', variant: 'success' },
  closed: { label: '已關閉', variant: 'destructive' },
  archived: { label: '已封存', variant: 'secondary' },
} as const

type FormCardProps = FormListActionHandlers & {
  form: FormUiContract
  folderNames: string[]
  folders: FormFolderContract[]
  canManageForms: boolean
}

export function FormCard({
  form,
  folderNames,
  folders,
  canManageForms,
  onMoveToFolder,
  onManage,
  onDuplicate,
  onStatusChange,
  onPreview,
  onEdit,
}: FormCardProps) {
  const sortable = useSortable({ id: form.id, disabled: !canManageForms })
  const status = statusMap[form.status]
  const coverUrl = form.welcomeScreen.coverUrl
  const coverFit = form.welcomeScreen.coverFit ?? 'cover'
  const coverPosition = `${form.welcomeScreen.coverFocalX ?? 50}% ${
    form.welcomeScreen.coverFocalY ?? 50
  }%`

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className={sortable.isDragging ? 'relative z-10 scale-[1.01] opacity-95' : undefined}
    >
      <Card className="overflow-hidden">
        {canManageForms && (
          <button
            type="button"
            aria-label={`拖移 ${form.title} 排序`}
            className="group flex h-8 w-full cursor-grab items-center justify-center border-b border-slate-200 bg-white transition-colors hover:bg-slate-50 active:cursor-grabbing"
            {...sortable.attributes}
            {...sortable.listeners}
          >
            <span className="h-1 w-16 rounded-full bg-slate-300 transition-colors group-hover:bg-slate-400" />
          </button>
        )}
        <button
          type="button"
          onClick={() => (canManageForms ? onEdit(form.id) : onPreview(form.id))}
          aria-label={canManageForms ? `編輯 ${form.title}` : `預覽 ${form.title}`}
          className="relative h-40 w-full overflow-hidden bg-slate-900 text-left"
          style={{
            background: `linear-gradient(135deg, ${form.themeConfig.buttonColor}, ${form.themeConfig.accentColor})`,
          }}
        >
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={form.welcomeScreen.coverAlt ?? `${form.title} 開始頁圖片`}
              className="h-full w-full"
              style={{ objectFit: coverFit, objectPosition: coverPosition }}
            />
          ) : (
            <div className="absolute inset-0 opacity-30">
              <div className="absolute -right-12 top-8 size-44 rounded-full border border-white" />
              <div className="absolute bottom-6 left-8 size-24 rotate-12 rounded-lg border border-white" />
            </div>
          )}
        </button>
        <CardContent className="p-4">
          <h3 className="line-clamp-2 font-semibold leading-6">{form.title}</h3>
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5"
            data-testid="form-card-status-row"
          >
            <Badge variant={status.variant}>{status.label}</Badge>
            {folderNames.length > 0
              ? folderNames.map((name) => (
                  <Badge key={name} variant="secondary">
                    {name}
                  </Badge>
                ))
              : null}
          </div>
          {folderNames.length === 0 ? (
            <div className="mt-3 flex h-5 items-center text-xs text-slate-400">未分類</div>
          ) : null}
          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <span>
              {form.responseCount} 回應 · {form.completionRate}%
            </span>
            <span>{formatRelativeTime(form.updatedAt)}</span>
          </div>
          <div
            className={
              canManageForms ? 'mt-4 grid grid-cols-4 gap-2' : 'mt-4 grid grid-cols-3 gap-2'
            }
          >
            {canManageForms && (
              <Button
                variant="outline"
                size="icon"
                title="編輯"
                aria-label={`編輯 ${form.title}`}
                onClick={() => onEdit(form.id)}
              >
                <Edit3 className="size-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              title="預覽"
              aria-label={`預覽 ${form.title}`}
              onClick={() => onPreview(form.id)}
            >
              <Eye className="size-4" />
            </Button>
            <SharePanel
              form={form}
              trigger={
                <Button variant="outline" size="icon" title="分享" aria-label={`分享 ${form.title}`}>
                  <Share2 className="size-4" />
                </Button>
              }
            />
            <div className="flex justify-center">
              <FormQuickActionsMenu
                form={form}
                folders={folders}
                canManageForms={canManageForms}
                onMoveToFolder={onMoveToFolder}
                onManage={onManage}
                onDuplicate={onDuplicate}
                onStatusChange={onStatusChange}
                onPreview={onPreview}
                onEdit={onEdit}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
